import React, { createRef, RefObject, useState } from 'react';
import Bucket, { IAnswerItem, IContext } from './Bucket';
import {
  IOpenItemsContext,
  OpenItemsContext,
} from 'store/context/OpenItemsContext';
import { monthIndexToTimestamp, smartDateDuration } from 'utils/date';
import { IAnalyseState, IQueryState, ReportOverride } from './ChartViewer';
import { smartNumericCompare } from 'utils/string';
import { notifyError } from 'components/Notify/Notify';

import {
  ActionIcon,
  ViewerBox,
  CollapseOrExpand,
  GridReportBox,
  ReportBox,
  ViewerBoxActions,
  DashboardLinkWrapper,
  More,
  DurationView,
  ChartTypeItem,
  ChartFilterWrapper,
  ChartFilterPopup,
} from './styles';
import messages from './Messages';
import ActionLink from 'components/ActionLink';
import ChartTitle from './ChartTitle';
import Icon from 'components/Icon';
import RelatedResponsesModal from './RelatedResponses/RelatedResponsesModal';
import AnalyseViaAIDrawer, {
  AnalyseViaAIDrawerData,
} from './AnalyseViaAIDrawer/AnalyseViaAIDrawer';
import ChartForCustomSeries, { getChartType } from './ChartForCustomSeries';
import BoxChart from './BoxChart';
import APopover from '../APopover';
import Button from '../Button/Button';
import {
  HintIcon,
  HintText,
  QuestionHintBox,
} from '../FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/styles';
import { black, blue, lightGray, red } from '../../utils/style/colors';
import { PeriodSelector } from './ChartSettingEditor';
import IconTypes from '../Icon/IconTypes';
import Select, { IOption } from '../Select/Select';
import { removeDuplicates } from '../../utils/happyFunctions';
import { cloneDeep } from 'lodash';
import { toPng } from 'html-to-image';
import { getBlob } from '../../utils/file';
import notify from '../Notify';
import { convertDigitToProperFormat } from '../../utils/number';
import HoverIcon from '../HoverIcon';
import Badge from '../Badge';
import {
  IProviders,
  ITags,
  ReportViewType,
} from 'store/providers/providerTypes';
import {
  ChartTypes,
  DashboardLink,
  ICheckPoint,
  IFileAnswerView,
  IIndicatorAnswerView,
  IMultipleChoiceAnswerView,
  IMultipleChoiceQuestionView,
  IQuestionReportField,
  IQuestionsView,
  IReportView,
  ISeries,
  ISurveyItem,
  ISurveyViewWithData,
  ITagReportField,
  SeriesOrder,
  TimeScale,
} from 'store/survey/surveyTypes';
import { IUsers } from 'store/users/usersType';
import { IOpenItem } from 'store/entity/IOpenItem';
import { IQueryFilter } from 'store/entity/IQueryFilter';
import { IAnalyticsQuery } from 'store/entity/IAnalyticsQuery';
import i18n, { getCalendarLocal } from '../../i18n';
import { IQueryBucket } from '../../store/entity/IQueryBucket';
import QuestionListReport, {
  findQuestionById,
  getMultipleChoiceQuestionInfo,
  getQuestionListInfo,
} from './QuestionListReport';
import ColorPicker from './RelatedResponses/ColorPicker';
import LocalIcon from 'ui/LocalIcon';
import LabelColorPicker from './LabelColorPicker';

type ComponentProps = {
  analyse?: IAnalyseState;
  checkpoints: ICheckPoint[];
  collapsedCharts: number[];
  collapseOrExpand: (type: 'expand' | 'collapse', reportId: number) => void;
  fields: Array<ITagReportField | IQuestionReportField>;
  loading: boolean;
  onChangeShowCheckPoints?: () => void;
  onPrint?: () => void;
  onExport?: (includeScore: boolean) => Promise<any>;
  providers: IProviders;
  reportItemMap: {
    [key: number]: { itemId: number; reportToken: string };
  };
  users: IUsers['map'];
  reportItem?: IOpenItem;
  report?: IReportView;
  reportTitle: string;
  timeScale?: TimeScale;
  timeLength?: number;
  requestQuery?: IQueryState;
  series: ISeries[];
  showCheckPoints: boolean;
  survey: ISurveyViewWithData;
  tags: ITags | null;
  groupId?: string;
  display?: ReportViewType;
  isOpen?: Set<string>;
  setOpenItems?: (isOpen?: Set<string>) => any;
  reportOverride?: ReportOverride;
  setOverride?: (values: { key: string; value: any }[]) => void;
  saveReport?: (report: IReportView) => Promise<any>;
  reports?: { [key: number]: IReportView };
  extraTagIds?: number[];
  embedded?: boolean;
  surveyItem?: ISurveyItem;
  simple?: boolean;
};

export const CustomizedAxisTick = (props: any) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="start"
        fill="#666"
        transform="rotate(-45)"
        direction="rtl"
      >
        {payload.value}
      </text>
    </g>
  );
};

type ComponentStates = {
  showResponsesModal: null | {
    itemId?: string | number;
    queryKey: string;
    itemId2?: number;
  };
  notCommittedTimeScale?: TimeScale;
  loadingExport: boolean;
  removedSeriesForPrint: string[];
  showAnalyseViaAIDrawer: boolean;
  analyseViaAIDrawerData: AnalyseViaAIDrawerData;
};

export interface IChartDataItem {
  __name: string;
  __id?: string | number;
  __target?: number;
  __type?: 'value' | 'percent';
  __baseLine?: number;
  __percentageOfRealization?: number;
  [key: string]: number | string | undefined;
}

// const divStyle = {
//   overflow: 'auto',
//   margin: '2em 0',
// };

function getReportBoundText(
  filter: IQueryFilter,
  timeScale: TimeScale | undefined
) {
  const start = filter.start;
  const end = filter.end;

  return smartDateDuration(
    new Date(start!),
    new Date(end!),
    false,
    timeScale === 'day' || timeScale === 'week',
    true,
    timeScale
  );
}

function getChartTypeIcon(chartType: ChartTypes | undefined): IconTypes {
  switch (chartType) {
    case 'line':
      return 'git-commit';
    case 'bar':
      return 'bar-chart-2';
    case 'table':
      return 'server';
    case 'tableRev':
      return 'server';
    case 'barRev':
      return 'bar-chart-2';
    case 'stack':
      return 'sliders';
    case 'stackRev':
      return 'sliders';
    case 'pie':
      return 'pie-chart';
    case null:
      return 'aperture';
  }
  return 'aperture';
}
function getOrderIcon(order: SeriesOrder | undefined): IconTypes {
  switch (order) {
    case 'ValueAsc':
      return 'trending-up';
    case 'ValueDesc':
      return 'trending-down';
    case 'Default':
      return 'shuffle';
  }
  return 'shuffle';
}

function ChartTypeSelector(props: {
  reportOverride?: ReportOverride;
  setOverride: (values: { key: string; value: any }[]) => void;
}) {
  return (
    <APopover
      arrow
      curveBorder
      placement="top"
      trigger="click"
      target={
        <a
          href="#!"
          className="notTransparent-on-hover"
          style={{
            display: 'flex',
          }}
        >
          <ActionLink
            style={{
              margin: '0 0.3em',
            }}
            className="notTransparent-on-hover"
            title={i18n.t('chartType', { ns: 'survey' }) as string}
          >
            <Icon
              type={getChartTypeIcon(props.reportOverride?.chartType)}
              color={props.reportOverride?.chartType ? red : black}
              style={{
                display: 'inline-block',
                transform: props.reportOverride?.chartType?.endsWith('Rev')
                  ? 'rotate(90deg)'
                  : '',
              }}
            />
          </ActionLink>
        </a>
      }
    >
      <div className="hover-area">
        {[
          'table',
          'tableRev',
          'line',
          'bar',
          'barRev',
          'stack',
          'stackRev',
          'pie',
          null,
        ].map((it: any) => {
          return (
            <ChartTypeItem
              key={it || 'default'}
              onClick={() => {
                props.setOverride?.([{ key: 'chartType', value: it }]);
              }}
              style={{ cursor: 'pointer', display: 'flex', gap: '0.5rem' }}
            >
              <Icon
                type={getChartTypeIcon(it)}
                color={
                  it === props.reportOverride?.chartType
                    ? red
                    : it
                      ? black
                      : black
                }
                style={{
                  display: 'inline-block',
                  transform: it?.endsWith('Rev') ? 'rotate(90deg)' : '',
                }}
              />{' '}
              {(it && (i18n.t(`${it}`, { ns: 'survey' }) as string)) ||
                (i18n.t('defaultChart', { ns: 'survey' }) as string)}
            </ChartTypeItem>
          );
        })}
      </div>
    </APopover>
  );
}
function OrderSelector(props: {
  reportOverride?: ReportOverride;
  setOverride: (values: { key: string; value: any }[]) => void;
}) {
  return (
    <APopover
      arrow
      curveBorder
      placement="top"
      trigger="click"
      target={
        <a
          href="#!"
          className="notTransparent-on-hover"
          style={{
            display: 'flex',
          }}
        >
          <ActionLink
            style={{
              margin: '0 0.3em',
            }}
            className="notTransparent-on-hover"
            title={i18n.t('order', { ns: 'survey' }) as string}
          >
            <Icon
              type={getOrderIcon(props.reportOverride?.order)}
              color={props.reportOverride?.order ? red : black}
              style={{
                display: 'inline-block',
              }}
            />
          </ActionLink>
        </a>
      }
    >
      <div className="hover-area">
        {['ValueAsc', 'ValueDesc', 'Default', null].map((it: any) => {
          return (
            <ChartTypeItem
              key={it || 'default'}
              onClick={() => {
                props.setOverride?.([{ key: 'order', value: it }]);
              }}
              style={{ cursor: 'pointer', display: 'flex', gap: '0.5rem' }}
            >
              <Icon
                type={getOrderIcon(it)}
                color={
                  it === props.reportOverride?.order ? red : it ? black : black
                }
                style={{
                  display: 'inline-block',
                }}
              />
              {(it && (i18n.t(it, { ns: 'survey' }) as string)) ||
                (i18n.t('unset', { ns: 'survey' }) as string)}
            </ChartTypeItem>
          );
        })}
      </div>
    </APopover>
  );
}

export function getFinalName(
  reportTitle: string,
  report: IReportView,
  timeScale: TimeScale,
  reportOverride?: ReportOverride
) {
  if (!reportOverride) return reportTitle;
  let finalReportTitle = reportTitle;
  const originalTimeScale = report?.time.timeScale || timeScale;
  if (
    (reportOverride?.timeScale ?? originalTimeScale) !== originalTimeScale ||
    (!report?.time.splitOnTime &&
      reportOverride.timeLength &&
      reportOverride.timeLength !== report?.time.timeLength)
  ) {
    let rangeName = messages.getRangeName(
      reportOverride!.timeScale || originalTimeScale
    );
    if (
      !report?.time.splitOnTime &&
      reportOverride.timeLength &&
      reportOverride.timeLength !== report?.time.timeLength
    )
      rangeName =
        convertDigitToProperFormat(reportOverride.timeLength!) +
        ' ' +
        rangeName;
    finalReportTitle = finalReportTitle.replace(
      messages.getRangeName(originalTimeScale),
      rangeName
    );
  }
  return finalReportTitle;
}
function TimeScaleSelector(props: {
  showBorder: boolean;
  reportBounds: string;
  report: IReportView | undefined;
  reportOverride: ReportOverride | undefined;
  override: ((values: { key: string; value: any }[]) => void) | undefined;
  onChangeTimeLength: (value: string) => void;
  notCommittedTimeScale: TimeScale | undefined;
  onChangeTimeScale: (value: TimeScale) => void;
  timeScale?: TimeScale;
  timeLength?: number;
}) {
  return (
    <APopover
      arrow
      curveBorder
      placement="bottom-start"
      trigger="click"
      target={
        <DurationView
          showBorder={props.showBorder}
          className={props.showBorder && 'notTransparent-on-hover'}
        >
          {props.showBorder ? (
            <Icon type={'calendar'} color={black} />
          ) : (
            props.reportBounds
          )}
        </DurationView>
      }
    >
      <div>
        {props.reportOverride && props.override && (
          <PeriodSelector
            timeLength={(
              props.reportOverride.timeLength ||
              props.report?.time.timeLength ||
              props.timeLength!
            ).toString()}
            onChangeTimeLength={props.onChangeTimeLength}
            timeScale={
              props.notCommittedTimeScale ||
              props.reportOverride.timeScale ||
              props.report?.time.timeScale ||
              props.timeScale!
            }
            onChangeTimeScale={props.onChangeTimeScale}
            customApplyOnEnter={true}
          />
        )}
      </div>
    </APopover>
  );
}

const QuestionFilterOverride: React.FC<any> = (props: {
  survey: ISurveyViewWithData;
  setOverride: (values: { key: string; value: any }[]) => void;
  reportOverride: ReportOverride | undefined;
  termFilter: IQueryFilter[];
  tags: ITags;
}) => {
  const [pendingOverrides, setPendingOverrides] = useState(
    undefined as { [key: number]: (string | number)[] } | undefined
  );
  const surveyVersion =
    props.survey?.viewingVersion || props.survey?.data?.currentVersion;

  const options: {
    [key: number]: {
      title: string;
      options: IOption[];
      selected: (number | string)[];
    };
  } = {};

  (
    Object.values(
      props.survey.data?.versions[surveyVersion]?.questionMap || []
    ) as IQuestionsView[]
  )
    .filter((q: IQuestionsView) => !q.deleted)
    .sort((a, b) => {
      return a.order - b.order;
    })
    .forEach((q) => {
      if (q.type !== 'multipleChoice') return;
      const choices: Array<number> = [];
      let choicesList: IMultipleChoiceQuestionView['choiceList'] = [];
      const question: IMultipleChoiceQuestionView | undefined =
        q && (q as IMultipleChoiceQuestionView);
      if (question?.useTags && props.tags) {
        // todo: parent tags could be null instead of array
        if (question.parentTagChoices) {
          question.parentTagChoices.forEach((parentId: number) => {
            //todo should check for null
            Object.values(props.tags.data)
              .filter(
                (tag) =>
                  tag.parentId === parentId &&
                  !tag.deleted &&
                  !question!.excludedTagChoices?.includes(Number(tag.id))
              )
              .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
              .map((item) => choices.push(Number(item.id)));
          });
        }
        if (question.tagChoices) {
          question.tagChoices.forEach((tagChoice) => {
            choices.push(tagChoice);
          });
        }
        // number[]
        choicesList = removeDuplicates(choices).map((tagId) => {
          // if (!props.tags.data[tagId])
          //   console.log('missing tag', tagId, question.title, choices);
          return {
            value: tagId.toString(),
            text: props.tags.data[tagId]?.text || tagId.toString(),
            score: 0,
          };
        });
      } else {
        choicesList = question?.choiceList || [];
      }
      const selected: (number | string)[] = [
        ...(props.reportOverride?.[question.id] || []),
      ];

      options[question.id] = {
        title: question.title,
        options: choicesList.map((it) => ({ value: it.value, label: it.text })),
        selected: removeDuplicates(
          pendingOverrides
            ? pendingOverrides[question.id] || []
            : [
                ...selected,
                ...props.termFilter.reduce(
                  (current, item) => {
                    if (
                      item.type === 'termFilter' &&
                      item.field === 'allAnswers'
                    ) {
                      item.values?.forEach(
                        (value: string | number | boolean) => {
                          if (value.toString().startsWith(question.id + '$$')) {
                            current.push(
                              value
                                .toString()
                                .substring(question.id.toString().length + 2)
                            );
                          }
                        }
                      );
                    }
                    return current;
                  },
                  [] as (number | string)[]
                ),
              ]
        ),
      };
    });

  const [isOpen, setOpen] = useState(false);
  return Object.keys(options).length ? (
    <APopover
      arrow
      curveBorder
      placement="bottom-start"
      trigger="click"
      show={isOpen}
      onShowChange={() => {
        setOpen(!isOpen);
      }}
      target={
        <a
          href="#!"
          className={
            props.reportOverride?.overrideFilters
              ? ''
              : 'notTransparent-on-hover'
          }
          style={{
            display: 'flex',
          }}
        >
          <ActionLink
            style={{
              margin: '0 0.3em',
            }}
            title={i18n.t('filters', { ns: 'survey' }) as string}
          >
            <Icon
              type={'filter'}
              color={props.reportOverride?.overrideFilters ? red : black}
            />
          </ActionLink>
        </a>
      }
    >
      <ChartFilterPopup>
        <div style={{ position: 'fixed', top: '10px', left: '10px' }}>
          <Button
            style={{ padding: '2px 4px', margin: '0px 10px' }}
            onClick={() => {
              props.setOverride([
                ...Object.entries(pendingOverrides || {}).map(
                  ([key, value]) => ({
                    key: key,
                    value: value,
                  })
                ),
                { key: 'overrideFilters', value: true },
              ]);
              setOpen(false);
            }}
            color={'black-outline'}
          >
            {i18n.t('apply', { ns: 'survey' }) as string}
          </Button>
          <Button
            style={{ padding: '2px 4px' }}
            onClick={() => {
              const changes: { key: string; value: any }[] = [];
              Object.keys(props.reportOverride || {}).forEach((it) => {
                if (Number(it) > 0)
                  changes.push({ key: it.toString(), value: undefined });
              });
              props.setOverride([
                ...changes,
                { key: 'overrideFilters', value: false },
              ]);
              setPendingOverrides(undefined);
              setOpen(false);
            }}
            color={'red-outline'}
          >
            {i18n.t('reset', { ns: 'survey' }) as string}
          </Button>
        </div>
        <ChartFilterWrapper>
          {Object.entries(options).map(([key, config]) => {
            return (
              <Select
                label={config.title}
                key={key}
                containerStyle={{ maxWidth: '300px' }}
                onChangeValues={(values) => {
                  const result = cloneDeep(
                    pendingOverrides || props.reportOverride || {}
                  );
                  result[key] = values.map((it) => it.value);
                  setPendingOverrides(result);
                }}
                options={config.options}
                values={config.options.filter((it) =>
                  config.selected.includes(it.value)
                )}
                multiChoice={true}
              />
            );
          })}
        </ChartFilterWrapper>
      </ChartFilterPopup>
    </APopover>
  ) : null;
};

/*views reports for a single report (multiple series series)*/
class SurveyChart extends React.Component<ComponentProps, ComponentStates> {
  state: ComponentStates = {
    showResponsesModal: null,
    loadingExport: false,
    removedSeriesForPrint: [],
    showAnalyseViaAIDrawer: false,
    analyseViaAIDrawerData: null,
  };

  private chartRefs: Array<React.RefObject<HTMLDivElement>>;
  private widthRefs: Array<React.RefObject<HTMLDivElement>>;
  constructor(props: ComponentProps) {
    super(props);
    this.chartRefs = (Object.keys(props.requestQuery || {}).map(
      (_) => React.createRef() as React.RefObject<HTMLDivElement>
    ) || []) as Array<React.RefObject<HTMLDivElement>>;
    this.widthRefs = (Object.keys(this.props.requestQuery || {}).map(
      (_) => React.createRef() as React.RefObject<HTMLDivElement>
    ) || []) as Array<React.RefObject<HTMLDivElement>>;
  }
  componentDidUpdate(prevProps: Readonly<ComponentProps>) {
    if (
      Object.keys(this.props.requestQuery || {}).length !==
      Object.keys(prevProps.requestQuery || {}).length
    ) {
      this.chartRefs = Object.keys(this.props.requestQuery || {}).map(() =>
        React.createRef()
      );
      this.widthRefs = Object.keys(this.props.requestQuery || {}).map(() =>
        React.createRef()
      );
    }
  }

  includeScore: RefObject<HTMLInputElement> = createRef();
  includePercentageOfRealisation: RefObject<HTMLInputElement> = createRef();

  openLink = (link: DashboardLink, context: IOpenItemsContext) => {
    if (!isNaN(Number(link.url))) {
      if (context.openItems.length)
        context.changeOpenItems(
          Number(link.url),
          (this.props.surveyItem?.providerId as number) ??
            (this.props.reportItem?.item.data.providerId as number)
        );
      else if (window.location.href.includes('/direct/')) {
        // in direct view
        window.open(
          window.location.href.substring(
            0,
            window.location.href.lastIndexOf('/') + 1
          ) + link.url
        );
      } // in Mobile view
      else
        window.open(
          window.location.href.split('?')[0] + `?item=${link.url}`,
          '_blank'
        );
    } else window.open(link.url, '_blank');
  };

  getChoiceId = (arrayName: string[]) => {
    if (arrayName[2]) {
      return arrayName[2];
    }
    return arrayName[1];
  };

  convertDeepMapToList = (
    ans: IAnswerItem[],
    series: ISeries
  ): IChartDataItem[] => {
    let foundNoneEmptyRow = false;
    let ret = ans.reduce((list: IChartDataItem[], item: IAnswerItem) => {
      const dataItem: IChartDataItem = {
        __name: item.__name,
        __target: item.__target,
        __baseLine: item.__baseLine,
        __percentageOfRealization: item.__percentageOfRealization,
        __type: item.__type,
        __id: item.__id,
      };
      if (
        typeof item.__value === 'number' ||
        typeof item.__value === 'undefined'
      ) {
        const metricName = messages.generateMetricName(series.metric);
        dataItem[metricName] = item.__value;
        dataItem['__percentageOfRealization' + metricName] =
          item.__percentageOfRealization;
        dataItem['__target' + metricName] = item.__target;
        // dataItem['__id' + metricName] = item.__id;
      } else {
        foundNoneEmptyRow = true;
        item.__value.forEach((ansItem) => {
          dataItem[ansItem.__name] = ansItem.__value as number;
          dataItem['__percentageOfRealization' + ansItem.__name] =
            ansItem.__percentageOfRealization as number;
          dataItem['__target' + ansItem.__name] = ansItem.__target as number;
          dataItem.__type = ansItem.__type || dataItem.__type;
          dataItem['__id' + ansItem.__name] = ansItem.__id;
        });
      }
      list.push(dataItem);
      return list;
    }, [] as IChartDataItem[]);
    if (foundNoneEmptyRow) {
      // remove data entered: dataItem[metricName] = item.__value;
      ret = ret.reduce((prev: IChartDataItem[], current: IChartDataItem) => {
        delete current[messages.generateMetricName(series.metric)];
        prev.push(current);
        return prev;
      }, [] as IChartDataItem[]);
    }
    return ret;
  };

  convertTotalToList = (ans: IAnswerItem[]): IChartDataItem[] => {
    const dataItem: IChartDataItem = {
      __name: i18n.t('totalValue', { ns: 'survey' }) as string,
    };

    ans.forEach((item) => {
      if (
        typeof item.__value === 'number' ||
        typeof item.__value === 'undefined'
      ) {
        dataItem[item.__name] = item.__value;
      } else {
        item.__value.forEach((ansItem) => {
          dataItem[ansItem.__name] = ansItem.__value as number;
        });
      }
    });

    return [dataItem];
  };

  handleShowCheckPoint = () => {
    if (this.props.onChangeShowCheckPoints) {
      this.props.onChangeShowCheckPoints();
    }
  };

  calculateOffset = (data: any[]): number => {
    const realizationList = data.map(
      (item) => item.__percentageOfRealization || 0
    );
    const max = Math.max(...realizationList);
    const min = Math.min(...realizationList);
    if (min > 0) {
      return 100;
    }
    if (max < 0) {
      return 0;
    }
    return max / (max - min);
  };

  handleShowRelatedResponsesModal =
    (queryKey: string | null, validSecondId?: boolean) =>
    (itemId?: number | any, itemId2?: number | any) => {
      if (window.innerWidth < 600) {
        // mobile view
        return;
      }
      if (typeof itemId === 'object') {
        itemId = (itemId as any).payload.__id;
      }
      if (typeof itemId2 === 'object') {
        itemId2 = (itemId2 as any).payload?.__id;
      }
      if (
        queryKey &&
        this.props.requestQuery?.[queryKey]?.buckets?.some(
          (it: IQueryBucket) => it.field === 'sqId'
        )
      )
        return;
      this.setState({
        showResponsesModal:
          queryKey === null
            ? null
            : {
                queryKey,
                itemId,
                itemId2: validSecondId ? itemId2 : undefined,
              },
      });
    };

  collapseOrExpand = (type: 'collapse' | 'expand', reportId: number) => () => {
    this.props.collapseOrExpand(type, reportId);
  };

  handleExport = () => {
    this.setState({ loadingExport: true });
    this.props.onExport!(this.includeScore!.current!.checked).then(
      () => {
        this.setState({ loadingExport: false });
      },
      (err) => {
        notifyError(err);
        this.setState({ loadingExport: false });
      }
    );
  };

  getAutoWidthHeight = (
    data: any,
    series: ISeries,
    report: IReportView | undefined
  ) => {
    const dashboardWidth = report?.dashboardWidth;
    let dashboardHeight = report?.dashboardHeight;
    if (dashboardHeight && dashboardWidth) {
      if (window.innerWidth < 450 && series.chartType === 'bar') {
        const maxTextLength = data[0].reduce((current: number, item: any) => {
          Object.entries(item).forEach(([key, value]) => {
            if (key === '__name' || !key.startsWith('__'))
              current = Math.max(current, (value as any)?.length ?? 0);
          });
          return current;
        }, 0);
        if (maxTextLength < 25) dashboardHeight = Math.min(dashboardHeight, 2);
      }
      return [dashboardWidth, dashboardHeight];
    }
    try {
      const width = data[0].length;
      if (width === 0) return [1, 1];
      let height = Object.values(data[0][0]).length - 5;
      if (height <= 1 && width <= 1) return [1, 1];
      if (series.metric === 'responseList') {
        height = data[0].length;
        const numberOfQuestions = series.questionId?.split(',')?.length || 0;
        if (numberOfQuestions >= 3) return [4, height];
        else return [numberOfQuestions, height];
      } else {
        if (series.chartType === 'table')
          return [Math.ceil(width / 4) + 1, Math.ceil(height / 7) + 1];
        if (series.chartType === 'tableRev')
          return [Math.ceil(height / 4) + 1, Math.ceil(width / 7) + 1];
        if (series.chartType === 'bar')
          return [Math.ceil((width * height) / 7) + 1, 2];
        if (series.chartType === 'barRev')
          return [2, Math.ceil((width * height) / 6) + 1];
        if (series.chartType === 'line') return [Math.ceil(width / 8) + 1, 2];
        return [3, 3];
      }
    } catch (e) {
      return [2, 2];
    }
  };
  // getAutoWidthHeight = (analyse: IAnalyseState, series: ISeries) => {
  //   if (analyse[0].categories === null) return [1, 1];
  //   const categories = .values(analyse[0].categories);
  //   if (categories.length == 0) return [1, 1];
  //   const buckets = (categories[0] as ICategories).buckets;
  //   if (buckets.length === 0) return [1, 1];
  //   const width = buckets.length;
  //   if (width <= 1) return [1, 1];
  //   let height = 1;
  //   const is2dim = !!buckets[0].categories;
  //   if (is2dim) {
  //     height = (.values(buckets[0].categories)[0] as ICategories).buckets
  //       .length;
  //   }
  //   if (series.chartType == 'table')
  //     return [Math.ceil(width / 3) + 1, Math.ceil(height / 6) + 2];
  //   if (series.chartType == 'tableRev')
  //     return [Math.ceil(height / 3) + 1, Math.ceil(width / 6) + 2];
  //
  //   return [3, 3];
  // };

  onRemoveFromPrint = (queryKey: string) => () => {
    this.setState({
      removedSeriesForPrint: [...this.state.removedSeriesForPrint, queryKey],
    });
  };

  handleOpenAnalyseViaAIDrawer = (data: AnalyseViaAIDrawerData) => {
    this.setState({
      showAnalyseViaAIDrawer: true,
      analyseViaAIDrawerData: data,
    });
  };

  handleCloseAnalyseViaAIDrawer = () => {
    this.setState({
      showAnalyseViaAIDrawer: false,
      analyseViaAIDrawerData: null,
    });
  };

  render() {
    const {
      analyse,
      collapsedCharts,
      fields,
      loading,
      onPrint,
      providers,
      users,
      reportItem,
      report,
      requestQuery,
      series,
      survey,
      tags,
      timeLength,
      timeScale,
      groupId,
      display,
      isOpen,
      setOpenItems,
      reportTitle,
      reportOverride,
      setOverride,
      surveyItem,
    } = this.props;
    const local = getCalendarLocal();
    const { loadingExport } = this.state;
    const reportId = report ? report.id : undefined;
    let filteredCheckpoints = this.props.checkpoints;
    const data: any = {};
    const seriesItems: { [key: string]: string[] } = {};
    const queryKeys = Object.keys(requestQuery || {});
    let questions: {
      [questionId: number]: IQuestionsView;
    } = {};
    let surveyMaxScore = 0;
    const finalReportTitle = getFinalName(
      reportTitle,
      report!,
      timeScale!,
      reportOverride
    );
    if (analyse && requestQuery /*&& !localCustomization*/) {
      if (survey?.data) {
        const surveyVersion =
          survey.viewingVersion || survey.data.currentVersion;

        questions = survey.data.versions[surveyVersion].questionMap!;

        surveyMaxScore = survey.data.maxScore!;
      }

      const context: IContext = {
        tags: tags!,
        questions,
        timeScale: reportOverride?.timeScale || timeScale,
        providers,
        checkPoints: filteredCheckpoints,
        fields,
        users,
        surveyMaxScore,
        local: surveyItem?.providerId
          ? providers.map?.[surveyItem?.providerId]?.data?.surveyOwnerProperties
              ?.localId || local
          : local,
      };
      queryKeys.forEach((queryKey) => {
        let chartData: IChartDataItem[] = [];
        const query = requestQuery?.[queryKey];
        if (query.type === 'responseList') {
          data[queryKey] = analyse?.[queryKey];
        } else if (query.buckets && query.buckets.length > 0) {
          const ans = Bucket.bucketOf(
            (query.buckets || [])[0],
            (query.metrics || [])[0],
            query.filters,
            analyse[queryKey],
            series[queryKey],
            context
          ).extractResponse(analyse[queryKey].categories);
          chartData = this.convertDeepMapToList(ans, series[queryKey]);

          // filter checkpoints to show just checkpoints in the selected date range
          if (query.buckets[0].field === 'effectiveJMonth') {
            const start = monthIndexToTimestamp(
              query.buckets[0].start as number,
              local
            );
            filteredCheckpoints = filteredCheckpoints.filter(
              (ch) => ch.date && ch.date >= start
            );
          } else if (query.buckets[0].field === 'effectiveDate') {
            const start = query.buckets[0].start as number;
            filteredCheckpoints = filteredCheckpoints.filter(
              (ch) => ch.date && ch.date >= start
            );
          }

          if (
            query.buckets[0].field !== 'effectiveDate' &&
            query.buckets[0].field !== 'effectiveJMonth'
          ) {
            // chartData = chartData.sort((a, b) => {
            //   if (a.__name < b.__name) {
            //     return -1;
            //   }
            //   if (a.__name > b.__name) {
            //     return 1;
            //   }
            //   return 0;
            // });
          }
        } else if (query.buckets && query.buckets.length === 0) {
          chartData = [{ __name: '' }]; // it's a simple hack to add an empty value into chart data. it'll pass the next condition to let chart renders info box for type of all series
          // TODO: rewrite this piece of shit code
        }
        const allKeys = new Set<string>();
        for (let i = 0; i < chartData.length; i++) {
          Object.keys(chartData[i] || {})
            .filter(
              (key) =>
                !key.startsWith('__name') &&
                !key.startsWith('__target') &&
                !key.startsWith('__baseLine') &&
                !key.startsWith('__percentageOfRealization') &&
                !key.startsWith('__id') &&
                !key.startsWith('__type')
            )
            .forEach((item) => allKeys.add(item));
        }
        let keys = Array.from(allKeys);
        if (query.buckets?.[0]?.subBuckets?.[0]?.field === 'instance')
          keys = keys.sort((a, b) => {
            if (a === (i18n.t('total', { ns: 'survey' }) as string)) return 1;
            if (b === (i18n.t('total', { ns: 'survey' }) as string)) return -1;
            return smartNumericCompare(a, b);
          });
        seriesItems[queryKey] = keys;
        if (query.type !== 'responseList') data[queryKey] = chartData;
      });
    }

    const YAxisLabel: string[] = [];
    if (series && series.length > 0) {
      series.forEach((s) => {
        const metricName = messages.generateMetricName(s.metric);
        YAxisLabel.push(metricName);
        if (survey?.data?.unit) {
          YAxisLabel.push(`${metricName} ${survey.data.unit}`);
        }
      });
    }
    const finalShowCheckPoints =
      this.props.showCheckPoints && filteredCheckpoints.length > 0;

    function seriesMatch(current: ISeries, base: ISeries) {
      return (
        current.type === base.type &&
        current.metric === base.metric &&
        current.questionId === base.questionId &&
        current.tagId === base.tagId &&
        current.tagIds === base.tagIds &&
        current.split === base.split &&
        current.subQuestionId === base.subQuestionId &&
        current.splitOnValues === base.splitOnValues &&
        current.splitOnFieldValues === base.splitOnFieldValues
      );
    }

    const matchSeriesList = series.reduce(
      (curr: boolean[], value: ISeries, index: number) => {
        if (index === 0) return [true];
        curr.push(seriesMatch(value, series[0]));
        return curr;
      },
      [] as boolean[]
    );
    const isCumulativeReport = requestQuery && queryKeys.length > 1;
    const isCollapsed = !!reportId && collapsedCharts.includes(reportId);

    const showDashboardGrid =
      display === 'Dashboard' && window.innerWidth > 500;
    const ItemBox = showDashboardGrid ? GridReportBox : ReportBox;
    const autoDim = this.getAutoWidthHeight(data, series[0], report) as [
      number,
      number,
    ];
    const showMore =
      (display !== 'List' &&
        isOpen &&
        requestQuery &&
        queryKeys
          .slice(0, 1) // first chart
          .map((queryKey) => {
            const query: IAnalyticsQuery = requestQuery[queryKey];
            if (query.buckets.length === 0) return false;

            const chartType = getChartType(
              series[queryKey],
              query,
              query.buckets[0],
              reportOverride
            );
            return (
              (chartType === 'table' &&
                Object.values(data?.[0]?.[0] || {}).length - 5 >
                  window.innerHeight / 35) ||
              (chartType === 'tableRev' &&
                Object.values(data?.[0] || {}).length - 5 >
                  window.innerHeight / 35) ||
              (chartType === 'barRev' &&
                Object.values(data?.[0] || {}).length - 5 >
                  window.innerHeight / 35)
            );
          })[0]) ||
      false;

    const reportBounds =
      requestQuery && requestQuery[0]
        ? getReportBoundText(
            requestQuery![0].filters.find(
              (it) => it.type === 'dateRangeFilter'
            )!,
            reportOverride?.timeScale || timeScale
          )
        : '';

    const extraFilterBadge =
      (Object.keys(requestQuery || {}).length > 0 &&
        requestQuery![
          Object.keys(requestQuery || {}).slice(0, 1)[0]
        ].extraFilterTitle?.reduce((current: Element[], item: string) => {
          return [
            ...current,
            <Badge style={{ marginRight: '5px' }} key={item}>
              {item}
            </Badge>,
          ];
        }, [])) ||
      '';
    const compactDate = display === 'Dashboard' && autoDim[0] === 1;
    const isAllQuestionListReport = Object.values(analyse || {}).every((item) =>
      Array.isArray(item)
    );

    const isPrinting = !this.props.onPrint;

    const reportLables =
      seriesItems?.[0]?.length > 1
        ? seriesItems?.[0]
        : seriesItems?.[0]?.includes(messages.totalAvg)
          ? data?.[0]?.map((el: any) => el['__name'])
          : series[0]?.chartType === 'pie'
            ? data?.[0]?.map((el: any) => el['__name'])
            : [];

    const hasEnabledAnalysis =
      surveyItem?.tags
        .filter((tag) => !tag?.deleted)
        .find((el) => el.text === 'conf_AIAnalysis') ||
      reportItem?.item?.data?.tags
        ?.filter((tag) => !tag?.deleted)
        .find((el) => el.text === 'conf_AIAnalysis');

    return (
      <ItemBox
        style={{
          gridColumnEnd: `span ${Math.min(
            autoDim[0],
            Math.floor(window.innerWidth / 185),
            6
          )}`,
          gridRowEnd: `span ${Math.min(
            autoDim[1],
            !isOpen || isOpen.has(reportId?.toString() || 'x')
              ? 100
              : Math.floor(window.innerHeight / 200)
          )}`,
          maxHeight:
            !showMore || !isOpen || isOpen.has(reportId?.toString() || 'x')
              ? '10000vh'
              : '90vh',
        }}
        key={reportId}
        isFullScreen={display === 'Fullscreen'}
        showMore={showMore && isOpen && isOpen?.has(reportId?.toString() || '')}
      >
        {loading ? (
          <span>loading...</span>
        ) : (
          <div
            className="hover-area"
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            {requestQuery && this.state.showResponsesModal !== null && (
              <RelatedResponsesModal
                isActive={!!this.state.showResponsesModal}
                onCloseModal={this.handleShowRelatedResponsesModal(null)}
                query={requestQuery[this.state.showResponsesModal.queryKey]}
                reportItem={reportItem}
                selectedReport={reportId}
                survey={survey}
                targetedItem={this.state.showResponsesModal}
                extraTagIds={this.props.extraTagIds}
                surveyItem={this.props.surveyItem}
                isInDashboardView={display === 'Dashboard'}
              />
            )}

            <AnalyseViaAIDrawer
              isOpen={this.state.showAnalyseViaAIDrawer}
              onClose={this.handleCloseAnalyseViaAIDrawer}
              data={this.state.analyseViaAIDrawerData}
              reportTitle={finalReportTitle || reportTitle || ''}
              surveyTitle={survey?.data?.name || ''}
            />

            {analyse && requestQuery && (
              <React.Fragment>
                {Object.keys(seriesItems).length > 0 ? (
                  <React.Fragment>
                    {onPrint && (
                      <ViewerBox>
                        <span style={{ color: black }}>
                          {finalReportTitle}
                          {extraFilterBadge}
                        </span>
                        {!this.props.simple && (
                          <ViewerBoxActions className="hide-on-mobile drop-shadow-on-hover">
                            <span>
                              {reportItem && report && report.surveyId && (
                                <OpenItemsContext.Consumer>
                                  {(context: any) => (
                                    <ActionIcon
                                      style={{
                                        margin: '0 0.3em',
                                      }}
                                      className="notTransparent-on-hover"
                                      title={
                                        i18n.t('showInTarget', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                      onClick={() => {
                                        const item =
                                          this.props.reportItemMap[reportId!]
                                            ?.itemId;
                                        if (item) context.changeOpenItems(item);
                                      }}
                                    >
                                      <Icon type="link" color={black} />
                                    </ActionIcon>
                                  )}
                                </OpenItemsContext.Consumer>
                              )}
                            </span>
                            {reportLables?.length > 0 && report && (
                              <span>
                                <APopover
                                  arrow
                                  curveBorder
                                  placement="bottom-start"
                                  trigger="click"
                                  target={
                                    <a
                                      href="#!"
                                      className="notTransparent-on-hover"
                                      style={{
                                        display: 'flex',
                                      }}
                                    >
                                      <ActionLink
                                        style={{
                                          margin: '0 0.3em',
                                        }}
                                        color="black"
                                        title={
                                          i18n.t('lableColorPicker', {
                                            ns: 'survey',
                                          }) as string
                                        }
                                      >
                                        <LocalIcon
                                          iconName="color-picker.svg"
                                          type="sm"
                                          color={'no-color'}
                                          isLink={true}
                                        />
                                      </ActionLink>
                                    </a>
                                  }
                                >
                                  <LabelColorPicker
                                    reportLables={reportLables}
                                    report={report}
                                    colorMap={report?.colorMap ?? {}}
                                    onSaveReport={this.props.saveReport}
                                  />
                                </APopover>
                              </span>
                            )}
                            {this.props.onPrint && (
                              <ActionLink
                                style={{
                                  margin: '0 0.3em',
                                }}
                                className="notTransparent-on-hover"
                                onClick={this.props.onPrint}
                                title={
                                  i18n.t('print', {
                                    ns: 'survey',
                                  }) as string
                                }
                              >
                                <Icon
                                  type="printer"
                                  className="mygreen-text"
                                  color={black}
                                />
                              </ActionLink>
                            )}
                            {hasEnabledAnalysis && (
                              <ActionLink
                                style={{}}
                                className="notTransparent-on-hover"
                                onClick={() =>
                                  this.handleOpenAnalyseViaAIDrawer(data)
                                }
                                title={
                                  i18n.t('AnalyseViaAI', {
                                    ns: 'survey',
                                  }) as string
                                }
                              >
                                <LocalIcon
                                  iconName="gemini-logo.svg"
                                  type="sm"
                                  color="no-color"
                                  isLink={true}
                                />
                              </ActionLink>
                            )}
                            {this.props.onExport && (
                              <APopover
                                arrow
                                curveBorder
                                placement="bottom-start"
                                trigger="click"
                                target={
                                  <a
                                    href="#!"
                                    className="notTransparent-on-hover"
                                    style={{
                                      display: 'flex',
                                    }}
                                  >
                                    <ActionLink
                                      style={{
                                        margin: '0 0.3em',
                                        // display: 'none',
                                      }}
                                      color="black"
                                      title={
                                        i18n.t('export', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    >
                                      <Icon
                                        type="download"
                                        className="mygreen-text"
                                        color={black}
                                      />
                                    </ActionLink>
                                  </a>
                                }
                              >
                                <div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '0.5rem',
                                      marginBottom: '1rem',
                                    }}
                                  >
                                    <h6>
                                      {
                                        i18n.t('copyImage', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    </h6>
                                    <Button
                                      onClick={this.copyChart()}
                                      loading={loadingExport}
                                      color="black"
                                    >
                                      <Icon type="copy" color={black} />
                                      {
                                        i18n.t('copy', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    </Button>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '0.5rem',
                                      marginBottom: '1rem',
                                    }}
                                  >
                                    <h6>
                                      {
                                        i18n.t('exportReportExcel', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    </h6>

                                    <p className="switch">
                                      <label>
                                        <input
                                          name="includePercentageOfRealization"
                                          id="includePercentageOfRealization"
                                          type="checkbox"
                                          ref={
                                            this.includePercentageOfRealisation
                                          }
                                        />
                                        <span
                                          className="lever"
                                          style={{
                                            marginInlineStart: '0',
                                            marginLeft: '10px',
                                          }}
                                        />
                                      </label>
                                      {
                                        i18n.t('percentageOfRealization', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    </p>

                                    <Button
                                      onClick={() => {
                                        this.handleExportReport(
                                          data,
                                          seriesItems,
                                          requestQuery,
                                          report?.name
                                        );
                                      }}
                                      loading={loadingExport}
                                      color="black-outline"
                                    >
                                      {
                                        i18n.t('downloadReport', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    </Button>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <h6>
                                      {
                                        i18n.t('exportResponseExcel', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    </h6>

                                    <p className="switch">
                                      <label>
                                        <input
                                          name="includeScore"
                                          id="includeScore"
                                          type="checkbox"
                                          ref={this.includeScore}
                                        />
                                        <span
                                          className="lever"
                                          style={{
                                            marginInlineStart: '0',
                                            marginLeft: '10px',
                                          }}
                                        />
                                      </label>
                                      {
                                        i18n.t('includeScore', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    </p>
                                    <Button
                                      onClick={this.handleExport}
                                      loading={loadingExport}
                                      color="black-outline"
                                    >
                                      {
                                        i18n.t('downloadRelatedResponses', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    </Button>
                                  </div>
                                </div>
                              </APopover>
                            )}
                            {this.props.setOverride && (
                              <>
                                {!isAllQuestionListReport && (
                                  <>
                                    <ChartTypeSelector
                                      reportOverride={reportOverride}
                                      setOverride={this.props.setOverride}
                                    />
                                    <div className="notTransparent-on-hover">
                                      <ColorPicker
                                        color={reportOverride?.chartColor || ''}
                                        onChangeColor={(color) =>
                                          this.props?.setOverride?.([
                                            { key: 'chartColor', value: color },
                                          ])
                                        }
                                        hideLabel={true}
                                        size="small"
                                      />
                                    </div>
                                    <OrderSelector
                                      reportOverride={reportOverride}
                                      setOverride={this.props.setOverride}
                                    />
                                  </>
                                )}
                                {survey && (
                                  <QuestionFilterOverride
                                    reportOverride={reportOverride}
                                    survey={survey}
                                    setOverride={this.props.setOverride}
                                    tags={tags}
                                    termFilter={requestQuery[0]?.filters || []}
                                  />
                                )}
                                <TimeScaleSelector
                                  showBorder={compactDate}
                                  reportBounds={reportBounds}
                                  report={report}
                                  reportOverride={reportOverride}
                                  override={setOverride}
                                  onChangeTimeLength={(value: string) => {
                                    const values = [
                                      {
                                        key: 'timeLength',
                                        value: Number(value) as any,
                                      },
                                    ];
                                    if (this.state.notCommittedTimeScale)
                                      values.push({
                                        key: 'timeScale',
                                        value: this.state.notCommittedTimeScale,
                                      });
                                    this.props.setOverride!(values);
                                  }}
                                  notCommittedTimeScale={
                                    this.state.notCommittedTimeScale
                                  }
                                  onChangeTimeScale={(value: TimeScale) => {
                                    this.setState({
                                      notCommittedTimeScale: value,
                                    });
                                  }}
                                  timeScale={this.props.timeScale}
                                  timeLength={this.props.timeLength}
                                />

                                <a
                                  href="#!"
                                  style={{
                                    display: 'flex',
                                    marginTop: '3px',
                                  }}
                                  className={'hideOnScreenshot'}
                                  onClick={this.copyChart()}
                                >
                                  <ActionLink
                                    style={{
                                      margin: '0 0.3em',
                                    }}
                                    title={
                                      i18n.t('copyImage', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                  >
                                    <Icon type={'copy'} color={black} />
                                  </ActionLink>
                                </a>
                              </>
                            )}
                          </ViewerBoxActions>
                        )}
                      </ViewerBox>
                    )}
                    {reportOverride?.loading && (
                      <div
                        className="col s12"
                        style={{
                          width: '100px',
                          top: '8px',
                          position: 'absolute',
                        }}
                      >
                        <div className="progress" style={{ margin: '0px' }}>
                          <div className="indeterminate" />
                        </div>
                      </div>
                    )}
                    {queryKeys
                      .slice(0, 1) // first chart
                      .map((queryKey, idx) => {
                        const query: IAnalyticsQuery = requestQuery[queryKey];
                        const currSeries: ISeries = series[queryKey];
                        if (this.state.removedSeriesForPrint.includes(queryKey))
                          return <div key={idx} />;
                        return (
                          <div
                            key={queryKey}
                            style={{
                              flex: 1,
                              pageBreakInside: 'avoid',
                              overflow: 'auto',
                            }}
                            ref={this.chartRefs[0]}
                          >
                            {!!query.filtersNotes?.length && (
                              <QuestionHintBox
                                color={lightGray}
                                style={{ padding: '4px 10px' }}
                              >
                                <HintIcon type="info" color={red} />
                                <HintText color={red}>
                                  <div style={{ color: '#000' }}>
                                    {
                                      i18n.t('permissionFilterApplied', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                  </div>
                                  <ul style={{ margin: 0 }}>
                                    {query.filtersNotes.map((it, index) => (
                                      <li key={index}>{it}</li>
                                    ))}
                                  </ul>
                                </HintText>
                              </QuestionHintBox>
                            )}

                            <ChartTitle
                              survey={survey?.data}
                              series={series[queryKey]}
                              report={
                                report ??
                                ({
                                  series,
                                  time: {
                                    timeScale: timeScale!,
                                    timeLength: timeLength!,
                                  },
                                } as IReportView)
                              }
                              extraFilters={extraFilterBadge}
                              saveReport={this.props.saveReport}
                              override={this.props.reportOverride}
                              tags={tags}
                              numberOfSeries={series.length}
                              reportTitle={this.props.reportTitle}
                              display={this.props.display}
                              onRemoveFromPrint={
                                isPrinting
                                  ? this.onRemoveFromPrint(queryKey)
                                  : undefined
                              }
                              reports={this.props.reports}
                            />
                            {query.type === 'responseList' ? (
                              <QuestionListReport
                                data={data?.[queryKey]}
                                survey={survey}
                                questionIdList={
                                  currSeries.questionId?.split(',') || []
                                }
                                tags={tags!}
                              />
                            ) : query.buckets.length === 0 ? (
                              <div
                                style={{
                                  flex: 1,
                                  display: 'flex',
                                  alignItems: 'flex-end',
                                  justifyContent: 'center',
                                  height: '100%',
                                }}
                              >
                                <BoxChart
                                  analyseMetrics={analyse[queryKey].metrics}
                                  queryMetric={query.metrics[0].metric}
                                  series={series[queryKey]}
                                  reportOverride={reportOverride}
                                />
                              </div>
                            ) : (
                              <React.Fragment>
                                {query.buckets.map((bucket, index) => {
                                  const chartType = getChartType(
                                    currSeries,
                                    query,
                                    bucket,
                                    reportOverride
                                  );
                                  return (
                                    <div
                                      key={index}
                                      style={{
                                        overflow: [
                                          'table',
                                          'tableRev',
                                        ].includes(chartType)
                                          ? 'visible'
                                          : 'auto',
                                        display:
                                          chartType === 'iranMap'
                                            ? 'inline'
                                            : '',
                                      }}
                                      ref={this.widthRefs[0] as any}
                                    >
                                      <ChartForCustomSeries
                                        survey={survey}
                                        compactAutoDim={autoDim}
                                        compact={display === 'Dashboard'}
                                        bucket={bucket}
                                        query={query}
                                        groupId={groupId}
                                        itemKey={reportId?.toString()}
                                        calculateOffset={this.calculateOffset}
                                        checkpoints={filteredCheckpoints}
                                        currSeries={currSeries}
                                        CustomizedAxisTick={CustomizedAxisTick}
                                        data={data}
                                        handleShowCheckPoint={
                                          this.handleShowCheckPoint
                                        }
                                        handleShowRelatedResponsesModal={
                                          this.handleShowRelatedResponsesModal
                                        }
                                        isPrinting={!this.props.onPrint}
                                        onChangeShowCheckPoints={
                                          this.props.onChangeShowCheckPoints
                                        }
                                        queryKey={queryKey}
                                        seriesItems={seriesItems}
                                        showCheckPoints={finalShowCheckPoints}
                                        YAxisLabel={YAxisLabel}
                                        reportTitle={reportTitle}
                                        reportOverride={reportOverride}
                                        report={report}
                                        simple={this.props.simple}
                                      />
                                    </div>
                                  );
                                })}
                              </React.Fragment>
                            )}
                          </div>
                        );
                      })}
                    {isCumulativeReport &&
                      !isCollapsed &&
                      display !== ('Dashboard' as ReportViewType) && (
                        <>
                          {queryKeys
                            .slice(1, queryKeys.length) // second item to end
                            .map((queryKey, index) => {
                              const query = requestQuery[queryKey];
                              const currSeries = series[queryKey];
                              const validCheckpoints =
                                matchSeriesList[queryKey];
                              if (
                                this.state.removedSeriesForPrint.includes(
                                  queryKey
                                ) /*||
                                !data[queryKey]?.length ||
                                !data[queryKey]?.find((it: any) =>
                                  .entries(it).find(
                                    (e) =>
                                      !e[0].startsWith('__') && isANumber(e[1])
                                  )
                                )*/
                              )
                                return <div key={queryKey} />;
                              return (
                                <div
                                  style={{ flex: 1, pageBreakInside: 'avoid' }}
                                  key={queryKey}
                                  ref={this.chartRefs[index + 1]}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-end',
                                    }}
                                  >
                                    <div style={{ flexGrow: 1 }}>
                                      <ChartTitle
                                        survey={survey?.data}
                                        series={series[queryKey]}
                                        tags={tags}
                                        report={
                                          report ??
                                          ({
                                            series,
                                            time: {
                                              timeScale: timeScale!,
                                              timeLength: timeLength!,
                                            },
                                          } as IReportView)
                                        }
                                        extraFilters={extraFilterBadge}
                                        saveReport={this.props.saveReport}
                                        override={this.props.reportOverride}
                                        numberOfSeries={series.length}
                                        reportTitle={this.props.reportTitle}
                                        onRemoveFromPrint={
                                          !this.props.onPrint
                                            ? this.onRemoveFromPrint(queryKey)
                                            : undefined
                                        }
                                        index={index + 1}
                                        reports={this.props.reports}
                                      />
                                    </div>

                                    {!isPrinting && (
                                      <a
                                        href="#!"
                                        style={{
                                          display: 'flex',
                                          margin: '3px 5px 10px 5px',
                                        }}
                                        className="hideOnScreenshot"
                                        onClick={this.copyChart(index + 1)}
                                      >
                                        <ActionLink
                                          style={{
                                            margin: '0 0.3em',
                                          }}
                                          title={
                                            i18n.t('copyImage', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                        >
                                          <HoverIcon
                                            type={'copy'}
                                            color={black}
                                          />
                                        </ActionLink>
                                      </a>
                                    )}
                                  </div>
                                  {query.type === 'responseList' ? (
                                    <QuestionListReport
                                      data={data?.[queryKey]}
                                      survey={survey}
                                      questionIdList={
                                        currSeries.questionId
                                          ?.split(',')
                                          ?.map((el: string) => {
                                            if (el.includes('@score')) {
                                              return el
                                                .split('@score')
                                                .at(0) as string;
                                            } else return el;
                                          }) || []
                                      }
                                      tags={tags!}
                                    />
                                  ) : query.buckets.length === 0 ? (
                                    <BoxChart
                                      analyseMetrics={analyse[queryKey].metrics}
                                      queryMetric={query.metrics[0].metric}
                                      series={series[queryKey]}
                                    />
                                  ) : (
                                    <React.Fragment>
                                      {query.buckets.map((_bucket: any) => {
                                        const chartType = getChartType(
                                          currSeries,
                                          query,
                                          _bucket,
                                          reportOverride
                                        );
                                        return (
                                          <div
                                            key={index}
                                            style={{
                                              overflow: [
                                                'table',
                                                'tableRev',
                                              ].includes(chartType)
                                                ? 'visible'
                                                : 'auto',
                                              display:
                                                chartType === 'iranMap'
                                                  ? 'inline'
                                                  : '',
                                            }}
                                            ref={this.widthRefs[index + 1]}
                                          >
                                            {
                                              <ChartForCustomSeries
                                                survey={survey}
                                                compact={
                                                  display ===
                                                  ('Dashboard' as ReportViewType)
                                                }
                                                simple={this.props.simple}
                                                bucket={_bucket}
                                                query={query}
                                                groupId={groupId}
                                                itemKey={reportId?.toString()}
                                                calculateOffset={
                                                  this.calculateOffset
                                                }
                                                checkpoints={
                                                  validCheckpoints
                                                    ? filteredCheckpoints
                                                    : []
                                                }
                                                currSeries={currSeries}
                                                CustomizedAxisTick={
                                                  CustomizedAxisTick
                                                }
                                                data={data}
                                                handleShowCheckPoint={
                                                  this.handleShowCheckPoint
                                                }
                                                handleShowRelatedResponsesModal={
                                                  this
                                                    .handleShowRelatedResponsesModal
                                                }
                                                isPrinting={isPrinting}
                                                onChangeShowCheckPoints={
                                                  this.props
                                                    .onChangeShowCheckPoints
                                                }
                                                queryKey={queryKey}
                                                seriesItems={seriesItems}
                                                showCheckPoints={
                                                  finalShowCheckPoints &&
                                                  validCheckpoints
                                                }
                                                YAxisLabel={YAxisLabel}
                                                reportTitle={reportTitle}
                                                reportOverride={reportOverride}
                                                report={report}
                                              />
                                            }
                                          </div>
                                        );
                                      })}
                                    </React.Fragment>
                                  )}
                                </div>
                              );
                            })}
                        </>
                      )}
                    {isCumulativeReport &&
                      series.length > 1 &&
                      display !== 'Dashboard' && (
                        <CollapseOrExpand
                          onClick={this.collapseOrExpand(
                            isCollapsed ? 'expand' : 'collapse',
                            reportId as number
                          )}
                        >
                          {isCollapsed
                            ? (i18n.t('showOtherCharts', {
                                ns: 'survey',
                              }) as string)
                            : (i18n.t('hideOtherCharts', {
                                ns: 'survey',
                              }) as string)}
                        </CollapseOrExpand>
                      )}
                  </React.Fragment>
                ) : (
                  <span>
                    {
                      i18n.t('thereIsNoValuesForTheReport', {
                        ns: 'survey',
                      }) as string
                    }
                  </span>
                )}
              </React.Fragment>
            )}

            {report?.dashboardLinks.length ? (
              <OpenItemsContext.Consumer>
                {(context) => (
                  <DashboardLinkWrapper>
                    {report?.dashboardLinks.map((link: any, index: number) => (
                      <ActionLink
                        onClick={() => {
                          this.openLink(link, context);
                        }}
                        color={'blue'}
                        style={{
                          border: `1px solid ${blue}`,
                          borderRadius: '10px',
                          padding: '3px 5px',
                        }}
                        key={index}
                      >
                        <Icon
                          style={{ margin: '0 0 0 2px' }}
                          type={
                            !isNaN(Number(link.url))
                              ? 'corner-down-left'
                              : 'external-link'
                          }
                        />
                        {link.name}
                      </ActionLink>
                    ))}
                  </DashboardLinkWrapper>
                )}
              </OpenItemsContext.Consumer>
            ) : undefined}
          </div>
        )}
        {showMore && (
          <More
            onClick={() => {
              if (isOpen && setOpenItems) {
                if (isOpen.has(reportId!.toString()))
                  isOpen.delete(reportId!.toString());
                else isOpen.add(reportId!.toString());
                setOpenItems(isOpen);
              }
            }}
          >
            {isOpen!.has(reportId!.toString())
              ? (i18n.t('less', { ns: 'survey' }) as string)
              : (i18n.t('more', { ns: 'survey' }) as string)}
          </More>
        )}
      </ItemBox>
    );
  }

  private copyChart(index: number = 0) {
    return () => {
      const headChartRef = this.chartRefs;
      if (headChartRef[index]?.current) {
        const width: number | undefined =
          this.widthRefs?.[index]?.current?.scrollWidth || undefined;
        const currentOverflow =
          this.widthRefs?.[index]?.current?.style?.overflow;
        if (width) this.widthRefs[index].current!.style.overflow = 'visible';
        document.querySelectorAll('.hideOnScreenshot').forEach((it: any) => {
          it.style.display = 'none';
        });
        document.querySelectorAll('.showOnScreenshot').forEach((it: any) => {
          it.style.display = 'block';
        });
        toPng(headChartRef[index].current!, { canvasWidth: width, width })
          .then((dataUrl) => {
            const pngImageBlob = getBlob(dataUrl);
            navigator.clipboard.write([
              new ClipboardItem({
                'image/png': pngImageBlob,
              }),
            ]);
            notify(i18n.t('copied', { ns: 'survey' }) as string);
            if (currentOverflow)
              this.widthRefs[index].current!.style.overflow = currentOverflow;
            document
              .querySelectorAll('.hideOnScreenshot')
              .forEach((it: any) => {
                it.style.display = 'flex';
              });

            document
              .querySelectorAll('.showOnScreenshot')
              .forEach((it: any) => {
                it.style.display = 'none';
              });
          })
          .catch(function () {
            /*do nothing*/
          });
      }
    };
  }

  handleExportReport = async (
    allData: { [key: string]: Required<IChartDataItem>[] },
    allSeries: { [key: string]: string[] },
    requestQuery: any,
    title?: string
  ) => {
    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();
    Object.keys(requestQuery).forEach((key) => {
      const data = allData[key] as any;
      const series = allSeries[key];
      const objMap: any[] = [];

      if (this.props.series?.[key].metric === 'responseList') {
        const uniqueQuestionIdList =
          this.props.series?.[key]?.questionId?.split(',');
        const [uniqueQuestionName, _] = getQuestionListInfo(
          data,
          this.props.survey,
          uniqueQuestionIdList
        );
        data.map((response: any) => {
          const itemObj: any = {};
          uniqueQuestionIdList.map((qId: string, indx: number) => {
            const foundedAnswer = response.surveyAnswerList?.find(
              (el: any) => el.questionId === qId
            );
            const foundedQuestion = findQuestionById(qId, this.props.survey);
            itemObj[uniqueQuestionName[indx]] = foundedAnswer
              ? foundedQuestion.type === 'multipleChoice'
                ? getMultipleChoiceQuestionInfo(
                    foundedAnswer as IMultipleChoiceAnswerView,
                    foundedQuestion,
                    this.props.tags!
                  )
                : foundedQuestion.type === 'file'
                  ? (foundedAnswer as IFileAnswerView)?.file?.url
                  : foundedQuestion.type === 'indicator'
                    ? `${(foundedAnswer as IIndicatorAnswerView).numerator}/${(foundedAnswer as IIndicatorAnswerView)?.denominator ?? 1}`
                    : foundedAnswer?.value
              : '';
          });
          objMap.push(itemObj);
        });
      } else {
        series.forEach((field) => {
          const itemObj: { [key: string]: string | number } = { '': field };
          data.forEach((item: any) => {
            const value = item[field] as number;
            const finalValue = typeof value === 'undefined' ? '-' : value;
            itemObj[item.__name] = finalValue;
          });
          objMap.push(itemObj);

          if (this.includePercentageOfRealisation.current?.checked) {
            const percentageOfRealizationItemObj: { [key: string]: string } = {
              '': `${i18n.t('percentageOfRealization', { ns: 'survey' })} ${field}`,
            };

            data.forEach((item: any) => {
              const percentOfRealizationValue =
                item[`__percentageOfRealization${field}`];
              const percentOfRealizationFinalValue =
                typeof percentOfRealizationValue === 'undefined'
                  ? '-'
                  : percentOfRealizationValue;
              percentageOfRealizationItemObj[item.__name] =
                percentOfRealizationFinalValue;
            });
            objMap.push(percentageOfRealizationItemObj);
          }
        });
      }
      const worksheet = XLSX.utils.json_to_sheet(objMap);
      XLSX.utils.book_append_sheet(workbook, worksheet, key);
    });
    XLSX.writeFile(workbook, (title || 'report') + '.xlsx');
    // const url = window.URL.createURL(new Blob([content]));
    // const effectiveFileName = 'export.csv';
    // const link = document.createElement('a');
    // link.href = url;
    // link.setAttribute('download', effectiveFileName); //or any other extension
    // document.body.appendChild(link);
    // link.click();
    // document.body.removeChild(link);
  };
}

export default SurveyChart;
